/**
 * Scroll-reveal orchestrator.
 *
 * Server-rendered markup tags blocks with `data-reveal`; this client component
 * adds the `js-reveal` class to <html> (so content stays fully visible when
 * JavaScript is unavailable) and reveals each tagged block once it enters the
 * viewport. Re-scans on route change because layouts persist while pages swap.
 */
import { useLocation } from "react-router";
import { useEffect } from "react";

export function ScrollReveal() {
  const { pathname } = useLocation();

  useEffect(() => {
    document.documentElement.classList.add("js-reveal");

    const elements = Array.from(
      document.querySelectorAll<HTMLElement>("[data-reveal]:not(.is-revealed)"),
    );
    if (elements.length === 0) return;

    if (!("IntersectionObserver" in window)) {
      for (const element of elements) element.classList.add("is-revealed");
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-revealed");
            observer.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" },
    );

    for (const element of elements) observer.observe(element);
    return () => observer.disconnect();
  }, [pathname]);

  return null;
}
